import { createClient } from 'contentful';

const client = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID || '',
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN || '',
  environment: process.env.REACT_APP_CONTENTFUL_ENVIRONMENT || 'master',
});

export interface ContentfulEntry {
  sys: {
    id: string;
    createdAt: string;
    updatedAt: string;
  };
  fields: {
    title: string;
    shortDescription: string;
    categoryCard: string;
    coverImage?: {
      sys: {
        id: string;
      };
    };
  };
}

export interface TrendingItem {
  id: string;
  category: string;
  timeAgo: string;
  title: string;
  description: string;
  source: string;
  imageUrl: string;
  link: string;
}

const getTimeAgo = (date: string): string => {
  const now = new Date();
  const past = new Date(date);
  const diffInHours = Math.floor((now.getTime() - past.getTime()) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'Just now';
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  
  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
};

export const fetchLatestTrending = async (): Promise<TrendingItem[]> => {
  try {
    const response = await client.getEntries({
      content_type: 'landingPage',
      order: ['-sys.updatedAt'],
      limit: 5,
    });

    const trendingItems: TrendingItem[] = await Promise.all(
      response.items.map(async (item: any) => {
        const fields = item.fields;
        let imageUrl = '';
        if (fields.coverImage?.sys?.id) {
          try {
            const asset = await client.getAsset(fields.coverImage.sys.id);
            imageUrl = `https:${(asset.fields as any).file.url}`;
          } catch (error) {
            console.error('Error fetching asset:', error);
          }
        }

        const category = fields.categoryCard?.split(',')[0]?.trim() || 'General';
        const slug = fields.title
          ?.toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'untitled';

        return {
          id: item.sys.id,
          category,
          timeAgo: getTimeAgo(item.sys.updatedAt),
          title: fields.title || 'Untitled',
          description: fields.shortDescription || '',
          source: 'Independent Press',
          imageUrl,
          link: `https://www.independentpress.com/article/${slug}`,
        };
      })
    );

    return trendingItems;
  } catch (error) {
    console.error('Error fetching trending data:', error);
    return [];
  }
};